// Logger utility.
//
// Supports two usage styles:
//   - Static:   Logger.info('msg')               (existing call sites)
//   - Instance: new Logger('Context').info(...)   (used across OCR / document / warranty services)
// The instance methods delegate to the static ones, prefixing messages with the instance context.
export class Logger {
  // Public + no private members so the class stays STRUCTURALLY compatible with plain
  // { info, error, warn, debug } logger objects (createLogger results) assigned to `Logger`-typed slots.
  context?: string;

  constructor(context?: string) {
    this.context = context;
  }

  // Instance methods (for `new Logger(name)` usage)
  info(message: string, context?: any) {
    Logger.info(this.context ? `[${this.context}] ${message}` : message, context);
  }

  error(message: string, error?: any, context?: any) {
    Logger.error(this.context ? `[${this.context}] ${message}` : message, error, context);
  }

  warn(message: string, context?: any) {
    Logger.warn(this.context ? `[${this.context}] ${message}` : message, context);
  }

  debug(message: string, context?: any) {
    Logger.debug(this.context ? `[${this.context}] ${message}` : message, context);
  }

  // Static methods (existing API)
  static info(message: string, context?: any) {
    console.log(`INFO: ${message}`, context);
  }

  static error(message: string, error?: any, context?: any) {
    console.error(`ERROR: ${message}`, error, context);
  }

  static warn(message: string, context?: any) {
    console.warn(`WARN: ${message}`, context);
  }

  static debug(message: string, context?: any) {
    console.debug(`DEBUG: ${message}`, context);
  }
}

// Export logger instance for compatibility
export const logger = {
  info: Logger.info,
  error: Logger.error,
  warn: Logger.warn,
  debug: Logger.debug,
};

// Export createLogger function for compatibility
export const createLogger = (name?: string) => ({
  info: (message: string, context?: any) => Logger.info(`[${name || 'Logger'}] ${message}`, context),
  error: (message: string, error?: any, context?: any) => Logger.error(`[${name || 'Logger'}] ${message}`, error, context),
  warn: (message: string, context?: any) => Logger.warn(`[${name || 'Logger'}] ${message}`, context),
  debug: (message: string, context?: any) => Logger.debug(`[${name || 'Logger'}] ${message}`, context),
});